import * as _ from 'lodash';

import { AvailableLanguage, brandAccessRightToProductKeyMap } from 'helpers';
import { FeaturedBrand } from 'shared/models/brand/types';
import { Brand, AccessState } from 'shared/models';
import { FcProductName } from 'types';

import { BrandData } from './providers/brand-access.service';
import {
    BrandAccessRight,
    BrandAccessRightTranslationsMap,
    FcProductPdfLinkTranslationsMap,
    FcProductTermsOfUse,
    ParticipatesInProductFlag,
    TermOfUseValue,
    TermsOfUse
} from './types';
import { AutoRestockBrand } from '../../smart-replenishment/retailer-platform/smart-replenishment/auto-restock/types';
import { SuggestionBrand } from '../../smart-replenishment/retailer-platform/smart-replenishment/suggestions/types';
import { filterBrandRelations } from './brand-search/utils';
import { LandingPage } from 'app/b2b-order/models/landing-page.model';

export function hasProductsTermsOfUse(brand: BrandData, requestedProducts: BrandAccessRight[]) {
    return _.some(
        _.map(
            requestedProducts,
            (product) => !_.isEmpty(_.get(brand, ['termsOfUse', FcProductTermsOfUse[product]], false))
        )
    );
}

export function prepareBrandRequestedProducts(
    brandTermsOfUse: TermsOfUse,
    requestedProductsWithTerms: BrandAccessRight[],
    userLanguage: AvailableLanguage,
    userOrganizationCountry: string
) {
    return _.uniqBy(
        _.map(requestedProductsWithTerms, (brandAccessRight) => {
            const productTermsOfUse = FcProductTermsOfUse[brandAccessRight];
            const termsOfUse: TermOfUseValue = _.get(brandTermsOfUse, productTermsOfUse);

            const fallbackTerms: string = _.get(termsOfUse, 'en', termsOfUse.de);
            const termsOfUseByLanguage: string = _.get(termsOfUse, userLanguage, fallbackTerms);
            const termsOfUseByCountry: string = _.get(
                termsOfUse,
                `countrySpecific.${userOrganizationCountry}`,
                termsOfUseByLanguage
            );

            return {
                name: productTermsOfUse,
                translationKey: BrandAccessRightTranslationsMap[productTermsOfUse],
                pdfLink: termsOfUseByCountry,
                pdfLinkTranslationKey: FcProductPdfLinkTranslationsMap[productTermsOfUse],
                checked: false
            };
        }),
        'name'
    );
}

export function getProductsWithTerms(
    brandTermsOfUse: TermsOfUse,
    requestedProducts: BrandAccessRight[]
): BrandAccessRight[] {
    return _.filter(
        requestedProducts,
        (product): product is Extract<BrandAccessRight, keyof TermsOfUse> =>
            !!_.get(brandTermsOfUse, FcProductTermsOfUse[product], false)
    );
}

export function getProductsBrandParticipates(brand: BrandData | FeaturedBrand) {
    return [
        BrandAccessRight.MarketingMaterial,
        BrandAccessRight.ProductData,
        ...(brand[ParticipatesInProductFlag.OrderPlatform] ? [BrandAccessRight.ReorderPlatform] : []),
        ...(brand[ParticipatesInProductFlag.PreorderPlatform] ? [BrandAccessRight.PreorderPlatform] : []),
        ...(brand[ParticipatesInProductFlag.EndlessAisle] ? [BrandAccessRight.EndlessAisle] : [])
    ];
}

export function getBrandFcProductsConnectionStatus(
    brand: Brand | BrandData | FeaturedBrand,
    brandsWithAutoRestockConnection: AutoRestockBrand[] = [],
    brandsWithSuggestionConnection: SuggestionBrand[] = [],
    landingPage?: LandingPage
) {
    const brandConnectedFcProducts: {
        product: FcProductName;
        status: AccessState;
    }[] = [];

    _.forEach(filterBrandRelations(brand.relations), (status, rightName) => {
        brandConnectedFcProducts.push({
            product: brandAccessRightToProductKeyMap[rightName],
            status
        });
    });

    if (brandsWithAutoRestockConnection.some(({ _id }) => _id === brand._id)) {
        brandConnectedFcProducts.push({
            product: FcProductName.AutoRestock,
            status: AccessState.Granted
        });
    }

    if (brandsWithSuggestionConnection.some(({ _id }) => _id === brand._id)) {
        brandConnectedFcProducts.push({
            product: FcProductName.Suggestions,
            status: AccessState.Granted
        });
    }

    if (landingPage) {
        brandConnectedFcProducts.push({
            product: FcProductName.Landing,
            status: AccessState.Granted
        });
    }

    return _.uniqBy(brandConnectedFcProducts, 'product');
}

export const getFilteredBrandsByTrialMode = ({
    brands,
    retailerId
}: {
    brands: (Brand | BrandData | FeaturedBrand)[];
    retailerId: string;
}) =>
    brands.filter((brand) => {
        return _.some(
            brand.fcProducts,
            (fcProductConfig) =>
                !fcProductConfig.trialPeriod.active || fcProductConfig.trialPeriod.allowedRetailers.includes(retailerId)
        );
    });
